<template>
  <ak-box
    size="small"
    class="one-whole h-full bt overflow-y"
    background="white"
  >
    <ak-loading v-model:active="loading" :can-cancel="false" />
    <ak-box size="small">
      <ak-form @submit="fetchContacts">
        <ak-input
          v-model="searchFilter"
          :loading="loading"
          placeholder="Search"
          type="search"
          icon="search"
          class="max-width-300"
          @right-icon-click="fetchContacts"
        />
      </ak-form>
    </ak-box>
    <ak-display-table v-if="contacts.length > 0" class="one-whole">
      <ak-display-table-row v-for="contact in contacts" :key="contact._id">
        <ak-display-table-cell
          tag="router-link"
          :to="{ name: 'contactsEdit', params: { id: contact._id } }"
          class="py-tiny pl-small"
        >
          <ak-pack>
            <ak-pack-item>
              <ak-avatar :string="contact.fullname" />
            </ak-pack-item>
            <ak-pack-item>
              <div>{{ contact.fullname }}</div>
              <div class="text-small">{{ contact.email[0].value }}</div>
            </ak-pack-item>
          </ak-pack>
        </ak-display-table-cell>
        <ak-display-table-cell
          tag="router-link"
          :to="{ name: 'contactsEdit', params: { id: contact._id } }"
          class="text-right pr"
        >
          <ak-tag
            v-if="
              contact.surveys.completed &&
              !contact.surveys.disqualified &&
              !contact.surveys.rejected
            "
            color="success"
          >
            Completed
          </ak-tag>
          <ak-tag
            v-if="contact.surveys.completed && contact.surveys.disqualified"
            color="danger"
          >
            Disqualified
          </ak-tag>
          <ak-tag
            v-if="contact.surveys.completed && contact.surveys.rejected"
            color="danger"
          >
            Rejected
          </ak-tag>
          <div v-if="contact.surveys.completed">
            <em>
              <small>
                on {{ formatDate(contact.surveys.completed_on, 'DD/MM/YYYY') }}
              </small>
            </em>
          </div>
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
    <ak-box
      v-if="!loading && contacts.length === 0"
      size="huge"
      class="text-center"
    >
      <ak-icon
        type="sad-outline"
        size="gigantic"
        color="grey-light"
        class="mb-tiny"
      />
      <h3 class="color-grey mb-tiny">There are currently no completions</h3>
    </ak-box>
  </ak-box>
</template>

<script>
import { defineComponent, onMounted, ref, computed } from 'vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Avatar from '@akerolabs/akero-vue/src/components/avatar/Avatar.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { formatDate } from '@akerolabs/akero-vue/src/filters/date'
import { setTabHistory } from '../utils'
export default defineComponent({
  name: 'CompletedTable',
  components: {
    [Box.name]: Box,
    [Form.name]: Form,
    [Input.name]: Input,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [Loading.name]: Loading,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Avatar.name]: Avatar,
    [Tag.name]: Tag,
    [Icon.name]: Icon,
  },
  props: {
    /**
     * @type {String}
     * @description Survey id to get completed for
     */
    id: {
      type: String,
      required: true,
    },
    /**
     * @type {Object}
     * @description The active account id
     */
    accountId: {
      type: String,
      required: true,
    },
    /**
     * @type {Object}
     * @description The active agency id
     */
    agencyId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const contacts = ref([])
    const loading = ref(true)
    const searchFilter = ref('')

    /**
     * @description An api ready request to get completed contacts
     * @type {Object}
     */
    const contactRequest = computed(() => {
      return {
        params: {
          ...searchRequest.value,
          account: props.accountId,
          agency: props.agencyId,
          surveys: JSON.stringify({
            $elemMatch: { survey_id: props.id, completed: true },
          }),
          overrideAccess: true,
        },
      }
    })

    /**
     * @description search filter for api request
     * @type {Object}
     */
    const searchRequest = computed(() => {
      if (searchFilter.value === '') {
        return {}
      }
      const query = { $regex: searchFilter.value, $options: 'i' }
      return {
        $or: JSON.stringify([{ fullname: query }, { 'email.value': query }]),
      }
    })

    /**
     * @description api request to get contact data
     */
    const fetchContacts = async () => {
      loading.value = true
      const { data } = await request.get('/contacts', contactRequest.value)
      for (let i = 0; i < data.data.length; i++) {
        data.data[i].surveys = data.data[i].surveys.find((survey) => {
          return survey.survey_id === props.id && survey.completed === true
        })
      }
      contacts.value = data.data
      loading.value = false
    }

    onMounted(async () => {
      await fetchContacts()
      setTabHistory('completed')
    })
    return {
      contacts,
      loading,
      searchFilter,
      formatDate,
      fetchContacts,
    }
  },
})
</script>
